import React, { useState, useEffect, useRef } from 'react'
import { useReactToPrint } from 'react-to-print'
import { API_BASE, ROUTER_BASENAME } from '../config'
import { mediaUrl, hideBrokenImg } from '../utils/mediaUrl'

const DEFAULT_RECEIPT = {
  receipt_name: 'श्री अरिहंत दिगंबर जैन ट्रस्ट, चिंचवड, पुणे.',
  receipt_address: 'प्लॉट नं. ४३०, सेक्टर नं. २५, प्राधिकरण, चिंचवड, पुणे - ४११०४४',
  receipt_registration_number: 'महा./१५००/२००९/पुणे',
  receipt_website: 'www.arihant.org.in',
  footer_contact: '',
  logo_path: '',
}

const ASSET_BASE = (ROUTER_BASENAME || '').replace(/\/$/, '')
const FALLBACK_LOGO = ASSET_BASE
  ? `${ASSET_BASE}/assets/images/logo.png`
  : '/assets/images/logo.png'

/** Colors matched to the official निधी-पावती sample */
const C = {
  cream: '#FFF8E7',
  red: '#C62828',
  redDeep: '#B71C1C',
  navy: '#1A237E',
  navyMid: '#283593',
  blue: '#1565C0',
  orange: '#E65100',
  bankBg: '#E3F2FD',
  ink: '#212121',
  line: '#C62828',
  muted: '#424242',
}

function formatDate(value) {
  if (!value) return ''
  const s = String(value)
  if (/^\d{2}[-/]\d{2}[-/]\d{4}/.test(s)) return s.split(/[ T]/)[0]
  const d = new Date(s)
  if (!Number.isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    return `${dd}-${mm}-${d.getFullYear()}`
  }
  return s
}

function buildAddress(data) {
  if (!data) return ''
  const parts = [
    data.address1,
  ].filter((p) => p != null && String(p).trim() !== '')
  return parts.join(', ')
}

function MotifBand() {
  const cells = Array.from({ length: 24 }, (_, i) => i)
  return (
    <div className="rp-motif" aria-hidden="true">
      {cells.map((i) => (
        <span key={i} className={i % 2 === 0 ? 'rp-motif-sw' : 'rp-motif-kl'}>
          {i % 2 === 0 ? '卐' : '✦'}
        </span>
      ))}
    </div>
  )
}

function PaymentReceipt(props) {
  const componentRef = useRef()
  const [receipt, setReceipt] = useState(DEFAULT_RECEIPT)
  const [settingsReady, setSettingsReady] = useState(false)
  const autoDownloadDone = useRef(false)
  const data = props.data || {}

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `receipt-${data.order_id || data.payment_id || 'donation'}`,
    onAfterPrint: () => {
      if (!props.autoDownload) alert('Print Success')
    },
  })

  useEffect(() => {
    let cancelled = false
    setSettingsReady(false)
    fetch(`${API_BASE}/settings/get`)
      .then((res) => res.json())
      .then((payload) => {
        if (cancelled) return
        if (payload.status === 'success' && payload.response) {
          const r = payload.response
          setReceipt({
            receipt_name: r.receipt_name || r.website_name || DEFAULT_RECEIPT.receipt_name,
            receipt_address: r.receipt_address || DEFAULT_RECEIPT.receipt_address,
            receipt_registration_number:
              r.receipt_registration_number || DEFAULT_RECEIPT.receipt_registration_number,
            receipt_website: r.receipt_website || DEFAULT_RECEIPT.receipt_website,
            footer_contact: r.footer_contact || '',
            logo_path: r.logo_path || '',
          })
        }
        setSettingsReady(true)
      })
      .catch(() => {
        if (!cancelled) setSettingsReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Lock background page scroll while the receipt modal is open
  useEffect(() => {
    const prevOverflow = document.body.style.overflow
    const prevPaddingRight = document.body.style.paddingRight
    const scrollbarGap = window.innerWidth - document.documentElement.clientWidth
    document.body.style.overflow = 'hidden'
    if (scrollbarGap > 0) {
      document.body.style.paddingRight = `${scrollbarGap}px`
    }
    return () => {
      document.body.style.overflow = prevOverflow
      document.body.style.paddingRight = prevPaddingRight
    }
  }, [])

  // Optional one-shot PDF download when opened via "Download PDF" action
  useEffect(() => {
    if (!props.autoDownload || !settingsReady || autoDownloadDone.current) return
    autoDownloadDone.current = true
    const t = setTimeout(() => {
      try {
        handlePrint()
      } catch (e) {
        /* ignore */
      }
    }, 400)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once after settings load
  }, [props.autoDownload, settingsReady])

  const logoSrc = mediaUrl(receipt.logo_path) || FALLBACK_LOGO
  const donorAddress = data.address || buildAddress(data)
  const donationLabel =
    data.type_name || data.donation_type_name || data.donation_type || 'Donation'
  const amountWords = data.amount_in_words || ''
  const receiptNo = data.order_id || data.payment_id || '—'
  const receiptDate = formatDate(data.created_at || data.date_added)
  const pan = data.pan || data.pan_number || data.panNumber || ''
  const mobile = data.mobile || ''
  const amountNum =
    data.payment_amount != null && data.payment_amount !== ''
      ? Number(data.payment_amount).toLocaleString('en-IN')
      : ''

  const mode = String(data.mode_of_payment || '').trim()
  const modeCash = !mode || /cash/i.test(mode)
  const modeOnline = /upi|neft|rtgs|online|card|basis/i.test(mode)
  const modeCheque = /cheque|check/i.test(mode)
  const chequeBits = [
    data.cheque_no ? `चेक नं. ${data.cheque_no}` : '',
    data.bank_name ? data.bank_name : '',
    data.transaction_id ? `Txn ${data.transaction_id}` : '',
  ]
    .filter(Boolean)
    .join(' · ')

  // Compact table: donation row + 2 blank lines (fits one landscape page)
  const tableRows = [
    { no: 1, label: donationLabel, amount: amountNum },
    { no: 2, label: '', amount: '' },
    { no: 3, label: '', amount: '' },
  ]

  const qrData = encodeURIComponent(
    [
      receipt.receipt_website || 'https://www.arihant.org.in',
      receiptNo ? `Receipt:${receiptNo}` : '',
      amountNum ? `Amt:${amountNum}` : '',
    ]
      .filter(Boolean)
      .join('|')
  )
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=110x110&margin=4&data=${qrData}`

  const modalStyle = {
    display: 'block',
    backgroundColor: 'rgba(0,0,0,0.8)',
  }

  return (
    <>
      <div
        className="modal receipt-modal-overlay show"
        style={modalStyle}
        role="dialog"
        aria-modal="true"
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <div className="modal-dialog modal-xl receipt-modal-dialog">
          <div className="modal-content receipt-modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <b>Payment Receipt</b>
              </h5>
              <button className="btn-close" type="button" onClick={props.hide}>
                <i className="fa fa-close"></i>
              </button>
            </div>
            <div className="modal-body receipt-modal-body">
              <style>{`
                .receipt-modal-overlay {
                  position: fixed !important;
                  inset: 0 !important;
                  z-index: 1055 !important;
                  overflow-x: hidden !important;
                  overflow-y: auto !important;
                  overscroll-behavior: contain;
                  padding: 1rem;
                  -webkit-overflow-scrolling: touch;
                }
                .receipt-modal-dialog {
                  max-width: min(1100px, 96vw);
                  margin: 0 auto 1.5rem;
                  pointer-events: auto;
                }
                .receipt-modal-content {
                  overflow: visible;
                }
                .receipt-modal-body {
                  overflow: visible !important;
                  max-height: none !important;
                  padding-bottom: 0.5rem;
                }
                .receipt-modal-footer {
                  border-top: 1px solid #dee2e6;
                  padding: 0.75rem 1rem;
                  background: #fff;
                }
                @media print {
                  .receipt-modal-footer,
                  .receipt-modal-overlay .modal-header {
                    display: none !important;
                  }
                }
              `}</style>
              <div ref={componentRef} className="rp-print-root">
                <style>{`
                  @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;600;700&family=Noto+Serif+Devanagari:wght@600;700&family=Tiro+Devanagari+Hindi:wght@400;600&display=swap');

                  .rp-print-root {
                    width: 100%;
                    max-width: 297mm;
                    margin: 0 auto;
                    font-family: 'Noto Sans Devanagari', 'Tiro Devanagari Hindi', Mangal, Kokila, 'Segoe UI', Arial, sans-serif;
                    color: ${C.ink};
                    background: #fff;
                  }
                  .rp-sheet {
                    background: ${C.cream};
                    border: 2px double ${C.red};
                    position: relative;
                    overflow: hidden;
                  }
                  .rp-motif {
                    display: flex;
                    flex-wrap: nowrap;
                    overflow: hidden;
                    background: ${C.cream};
                    border-bottom: 1px solid ${C.red};
                    height: 14px;
                  }
                  .rp-motif.bottom { border-bottom: 0; border-top: 1px solid ${C.red}; }
                  .rp-motif span {
                    flex: 1 0 4%;
                    text-align: center;
                    font-size: 8px;
                    line-height: 13px;
                    border-right: 1px solid rgba(198,40,40,0.35);
                    color: ${C.redDeep};
                  }
                  .rp-motif-kl { color: #8D6E63; font-size: 7px; }

                  .rp-topbar {
                    display: flex;
                    justify-content: space-between;
                    gap: 8px;
                    padding: 3px 10px 1px;
                    font-size: 7.5px;
                    line-height: 1.25;
                  }
                  .rp-topbar .left { color: ${C.red}; max-width: 55%; text-align: left; }
                  .rp-topbar .right { color: ${C.ink}; max-width: 42%; text-align: right; }

                  .rp-header {
                    display: grid;
                    grid-template-columns: 64px 1fr 64px;
                    gap: 8px;
                    align-items: center;
                    padding: 2px 10px 4px;
                  }
                  .rp-photo {
                    width: 64px;
                    height: 78px;
                    object-fit: cover;
                    border: 1px solid ${C.red};
                    background: #fff;
                  }
                  .rp-header-center { text-align: center; }
                  .rp-badge {
                    display: inline-block;
                    background: ${C.navy};
                    color: #fff;
                    font-weight: 700;
                    font-size: 12px;
                    padding: 2px 18px;
                    border-radius: 999px;
                    margin-bottom: 2px;
                  }
                  .rp-tagline { color: ${C.orange}; font-size: 9px; font-weight: 700; margin-bottom: 1px; }
                  .rp-trust-name {
                    color: ${C.redDeep};
                    font-family: 'Noto Serif Devanagari', 'Tiro Devanagari Hindi', Mangal, serif;
                    font-size: 16px;
                    font-weight: 700;
                    line-height: 1.2;
                    margin: 1px 0 2px;
                  }
                  .rp-subhead { color: ${C.navyMid}; font-size: 10px; font-weight: 700; margin-bottom: 2px; }
                  .rp-contact { color: ${C.muted}; font-size: 8.5px; line-height: 1.3; white-space: pre-line; }
                  .rp-contact .web { color: ${C.blue}; font-weight: 600; }

                  .rp-bank {
                    background: ${C.bankBg};
                    border-top: 1px solid ${C.blue};
                    border-bottom: 1px solid ${C.blue};
                    padding: 3px 10px;
                    font-size: 8.5px;
                    color: ${C.ink};
                    line-height: 1.3;
                    text-align: center;
                  }

                  .rp-body { position: relative; padding: 6px 10px 8px; min-height: 0; }
                  .rp-watermark {
                    position: absolute;
                    left: 50%;
                    top: 45%;
                    transform: translate(-50%, -50%);
                    width: 180px;
                    max-width: 40%;
                    opacity: 0.06;
                    pointer-events: none;
                    z-index: 0;
                  }
                  .rp-body > *:not(.rp-watermark) { position: relative; z-index: 1; }

                  .rp-info {
                    display: grid;
                    grid-template-columns: 1fr 140px;
                    gap: 8px;
                    margin-bottom: 4px;
                  }
                  .rp-fields .line {
                    display: flex;
                    align-items: flex-end;
                    gap: 5px;
                    margin-bottom: 4px;
                    font-size: 11px;
                    min-height: 16px;
                  }
                  .rp-fields .lbl { color: ${C.navy}; font-weight: 700; white-space: nowrap; flex-shrink: 0; }
                  .rp-fields .val {
                    flex: 1;
                    border-bottom: 1px solid ${C.line};
                    padding: 0 3px 1px;
                    min-height: 14px;
                    color: ${C.ink};
                  }
                  .rp-side { border-left: 1px solid ${C.red}; padding-left: 8px; font-size: 11px; }
                  .rp-side .box { margin-bottom: 6px; }
                  .rp-side .lbl { color: ${C.navy}; font-weight: 700; display: block; margin-bottom: 1px; font-size: 10px; }
                  .rp-side .num {
                    color: ${C.blue};
                    font-weight: 700;
                    font-size: 14px;
                    border-bottom: 1px solid ${C.line};
                    min-height: 18px;
                    padding-bottom: 1px;
                  }
                  .rp-side .date {
                    border-bottom: 1px solid ${C.line};
                    min-height: 16px;
                    color: ${C.ink};
                    padding-bottom: 1px;
                    font-size: 11px;
                  }

                  .rp-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 10.5px;
                    margin: 3px 0 4px;
                  }
                  .rp-table th, .rp-table td {
                    border: 1px solid ${C.red};
                    padding: 3px 6px;
                    vertical-align: middle;
                  }
                  .rp-table th {
                    color: ${C.navy};
                    font-weight: 700;
                    text-align: center;
                    background: rgba(198,40,40,0.06);
                  }
                  .rp-table .c-no { width: 8%; text-align: center; color: ${C.navy}; font-weight: 700; }
                  .rp-table .c-desc { width: 62%; color: ${C.navyMid}; }
                  .rp-table .c-amt { width: 30%; text-align: right; font-weight: 700; color: ${C.ink}; }
                  .rp-table .total-row td {
                    font-weight: 700;
                    color: ${C.navy};
                    background: rgba(198,40,40,0.06);
                  }

                  .rp-paymode {
                    font-size: 10.5px;
                    color: ${C.navy};
                    font-weight: 700;
                    margin: 3px 0 4px;
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px 12px;
                    align-items: center;
                  }
                  .rp-paymode .opt { color: ${C.ink}; font-weight: 600; }
                  .rp-paymode .opt.on { color: ${C.redDeep}; text-decoration: underline; }

                  .rp-words {
                    display: flex;
                    align-items: flex-end;
                    gap: 6px;
                    margin-bottom: 6px;
                    font-size: 11px;
                  }
                  .rp-words .lbl { color: ${C.navy}; font-weight: 700; white-space: nowrap; }
                  .rp-words .val {
                    flex: 1;
                    border-bottom: 1px solid ${C.line};
                    min-height: 15px;
                    padding: 0 3px 1px;
                  }

                  .rp-footer {
                    display: grid;
                    grid-template-columns: 110px 1fr 1fr 72px;
                    gap: 8px;
                    align-items: end;
                  }
                  .rp-rupee-box {
                    border: 1.5px solid ${C.navy};
                    background: #fff;
                    min-height: 36px;
                    display: flex;
                    align-items: center;
                    padding: 2px 6px;
                    gap: 4px;
                  }
                  .rp-rupee-box .sym { color: ${C.navy}; font-size: 20px; font-weight: 700; line-height: 1; }
                  .rp-rupee-box .amt { font-size: 13px; font-weight: 700; color: ${C.ink}; }
                  .rp-sig { text-align: center; font-size: 9.5px; color: ${C.navy}; font-weight: 700; }
                  .rp-sig .space { height: 22px; }
                  .rp-sig .line { border-top: 1px solid ${C.red}; padding-top: 2px; }
                  .rp-qr {
                    width: 68px;
                    height: 68px;
                    border: 1px solid ${C.red};
                    background: #fff;
                    object-fit: contain;
                    justify-self: end;
                  }

                  @media print {
                    @page { size: A4 landscape; margin: 4mm; }
                    html, body {
                      height: auto !important;
                      margin: 0 !important;
                      padding: 0 !important;
                      -webkit-print-color-adjust: exact;
                      print-color-adjust: exact;
                    }
                    .rp-print-root { max-width: none !important; width: 100% !important; }
                    .rp-sheet {
                      transform: scale(0.92);
                      transform-origin: top center;
                      page-break-inside: avoid;
                      break-inside: avoid;
                    }
                  }
                `}</style>

                <div className="rp-sheet">
                  <MotifBand />

                  <div className="rp-topbar">
                    <div className="left">
                      Income Tax Act 1961 Section 80G — DI No. AAO TS 1209 HE 2021901 /
                      UR No. AAO TS 1209 HE 20219; DI No. AAO TS 1209 HF 2021701 /
                      UR No. AAO TS 1209 HF 20217
                    </div>
                    <div className="right">
                      Trust Reg. No.: {receipt.receipt_registration_number}
                      <br />
                      {receipt.footer_contact
                        ? `Contact: ${receipt.footer_contact}`
                        : null}
                    </div>
                  </div>

                  <div className="rp-header">
                    <img
                      className="rp-photo"
                      src={logoSrc}
                      alt=""
                      onError={hideBrokenImg}
                    />
                    <div className="rp-header-center">
                      <div className="rp-badge">निधी-पावती</div>
                      <div className="rp-tagline">सहयोग · संघटन · सुसंस्कार</div>
                      <div className="rp-trust-name">{receipt.receipt_name}</div>
                      <div className="rp-subhead">श्री दिगंबर जैन सिद्धक्षेत्र सेवा</div>
                      <div className="rp-contact">
                        {receipt.receipt_address}
                        {receipt.footer_contact ? (
                          <>
                            <br />
                            मो.: {receipt.footer_contact}
                          </>
                        ) : null}
                        {receipt.receipt_website ? (
                          <>
                            <br />
                            <span className="web">{receipt.receipt_website}</span>
                          </>
                        ) : null}
                      </div>
                    </div>
                    <img
                      className="rp-photo"
                      src={logoSrc}
                      alt=""
                      onError={hideBrokenImg}
                    />
                  </div>

                  <div className="rp-bank">
                    Donations are eligible under Section 80G. Please quote receipt number
                    for correspondence.
                    {receipt.receipt_website
                      ? ` · Online: ${receipt.receipt_website}`
                      : ''}
                  </div>

                  <div className="rp-body">
                    <img
                      className="rp-watermark"
                      src={logoSrc}
                      alt=""
                      onError={hideBrokenImg}
                    />

                    <div className="rp-info">
                      <div className="rp-fields">
                        <div className="line">
                          <span className="lbl">नाम :</span>
                          <span className="val">{data.name || ''}</span>
                        </div>
                        <div className="line">
                          <span className="lbl">पत्ता :</span>
                          <span className="val">{donorAddress}</span>
                        </div>
                        <div className="line">
                          <span className="lbl">फोन :</span>
                          <span className="val">{mobile}</span>
                          <span className="lbl">पॅन नं. :</span>
                          <span className="val">{pan}</span>
                        </div>
                      </div>
                      <div className="rp-side">
                        <div className="box">
                          <span className="lbl">नं. :</span>
                          <div className="num">{receiptNo}</div>
                        </div>
                        <div className="box">
                          <span className="lbl">दिनांक :</span>
                          <div className="date">{receiptDate}</div>
                        </div>
                      </div>
                    </div>

                    <table className="rp-table">
                      <thead>
                        <tr>
                          <th className="c-no">नं.</th>
                          <th className="c-desc">विवरण</th>
                          <th className="c-amt">रुपये</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableRows.map((row) => (
                          <tr key={row.no}>
                            <td className="c-no">{row.no}</td>
                            <td className="c-desc">{row.label}</td>
                            <td className="c-amt">{row.amount}</td>
                          </tr>
                        ))}
                        <tr className="total-row">
                          <td colSpan={2} style={{ textAlign: 'right' }}>
                            एकूण
                          </td>
                          <td className="c-amt">{amountNum}</td>
                        </tr>
                      </tbody>
                    </table>

                    <div className="rp-paymode">
                      <span>भरणा पद्धत :</span>
                      <span className={`opt${modeCash ? ' on' : ''}`}>रोख</span>
                      <span>/</span>
                      <span className={`opt${modeOnline ? ' on' : ''}`}>ऑनलाईन</span>
                      <span>/</span>
                      <span className={`opt${modeCheque ? ' on' : ''}`}>
                        चेक नं.{chequeBits ? ` ${chequeBits}` : ''}
                      </span>
                    </div>

                    <div className="rp-words">
                      <span className="lbl">अक्षरी रुपये :</span>
                      <span className="val">
                        {amountWords
                          ? `${amountWords}${/only$/i.test(amountWords) ? '' : ' Only'}`
                          : ''}
                      </span>
                    </div>

                    <div className="rp-footer">
                      <div className="rp-rupee-box">
                        <span className="sym">₹</span>
                        <span className="amt">{amountNum}</span>
                      </div>
                      <div className="rp-sig">
                        <div className="space" />
                        <div className="line">देनेवाले के हस्ताक्षर</div>
                      </div>
                      <div className="rp-sig">
                        <div className="space" />
                        <div className="line">प्राप्तकर्ता के हस्ताक्षर</div>
                      </div>
                      <img className="rp-qr" src={qrSrc} alt="QR" />
                    </div>
                  </div>

                  <MotifBand />
                </div>
              </div>
            </div>
            <div className="modal-footer receipt-modal-footer justify-content-center flex-wrap gap-2">
              <button
                type="button"
                onClick={handlePrint}
                className="btn btn-rounded btn-primary"
              >
                <i className="fa fa-file-pdf-o me-1" aria-hidden="true" />
                Download PDF
              </button>
              <button
                type="button"
                onClick={handlePrint}
                className="btn btn-rounded btn-outline-secondary"
              >
                <i className="fa fa-print me-1" aria-hidden="true" />
                Print
              </button>
              <button
                type="button"
                onClick={props.hide}
                className="btn btn-rounded btn-outline-dark"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default PaymentReceipt
